import { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import ConditionalTooltip from '../conditionalTooltip';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import UserCount from '../userCount';
import ListWithTooltip from '../listWithTooltip';
import RichTextPreview from '../richTextPreview';
import ActionButton from '../actionButton';
import { useCustomEffect } from '../../../utility/hooks';
import { isEllipsisActive } from '../../../utility/helpers';
import { ellipsis } from '../../../constants/helperCssRules';
import { KEY_JOB_TYPES } from '../../../constants/succession';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';

const VISIBLE_ITEMS = 3;

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    cursor: 'pointer',
    boxSizing: 'border-box',
    border: `1px solid ${primary.bluish7}`,
    borderRadius: 4,
    width: '100%',
    minWidth: 280,
    minHeight: 241,
    padding: spacing(6),
    transition: 'border .2s ease',
    '&:hover': {
      border: `1px solid ${primary.blue1}`,
    },
  },
  rootSuccession: {
    minHeight: 293,
  },
  readOnly: {
    cursor: 'auto',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    maxWidth: 158,
    ...ellipsis(),
  },
  userCount: {
    marginLeft: spacing(1),
  },
  labelWrapper: {
    lineHeight: '20px',
    marginBottom: spacing(1),
  },
  label: {
    color: primary.bluish4,
  },
  meta: {
    display: 'flex',
    marginTop: spacing(2),
  },
  metaItem: {
    flex: 1,
    minWidth: 0,
  },
  metaValue: {
    color: primary.bluish3,
    marginTop: spacing(1),
    ...ellipsis(),
  },
  descriptionWrapper: {
    margin: spacing(2, 0),
    height: 95,
  },
  descriptionLabelWrapper: {
    minHeight: 30,
  },
  description: {
    color: primary.bluish3,
    '-webkit-line-clamp': 3,
    wordBreak: 'break-word',
  },
  tags: {
    marginTop: spacing(2),
  },
  tagsList: {
    gridTemplateColumns: `repeat(${VISIBLE_ITEMS}, minmax(0,min-content)) minmax(auto, min-content)`,
  },
  disabledUserCount: {
    backgroundColor: 'unset',
    cursor: 'default',
  },
  disabledButton: {
    pointerEvents: 'none',
  },
}));

const JobTitleItem = ({
  familyLabel,
  criticalityLabel,
  data,
  labels,
  getUsers,
  canSeeSuccession,
  canAccessUserProfileCheck,
  isReadOnly,
  hasAddButton,
  onAddUser,
  goToUserProfilePage,
  handlePreviewClick,
  onJobTitleClick,
}) => {
  const classes = useStyles();
  const titleRef = useRef(null);

  const [hasTitleTooltip, setHasTitleTooltip] = useState(false);

  const peopleCount = data.people_count;
  const isButtonDisabled = !peopleCount && !hasAddButton;

  const getVirtualTagItemSize = useCallback(
    (index, items) => (index === items.length - 1 ? 18 : 26),
    []
  );

  useCustomEffect(() => {
    setHasTitleTooltip(isEllipsisActive(titleRef?.current));
  }, [data.name]);

  const renderTitle = () => (
    <div className={classes.titleWrapper}>
      <ConditionalTooltip addTooltip={hasTitleTooltip} message={data.name}>
        <Typography
          ref={titleRef}
          variant="subtitle2"
          className={classes.title}
        >
          {data.name}
        </Typography>
      </ConditionalTooltip>
      <UserCount
        className={classNames(classes.userCount, {
          [classes.disabledButton]: isButtonDisabled,
        })}
        customUserCountBadgeClassName={classNames({
          [classes.disabledUserCount]: isButtonDisabled,
        })}
        userCount={peopleCount}
        canAccessUserProfileCheck={canAccessUserProfileCheck}
        getUsers={getUsers}
        goToUserProfile={goToUserProfilePage}
        actionButtonLabel={labels.assignTo}
        onTooltipAction={onAddUser}
        hasActionButton={hasAddButton}
      />
    </div>
  );

  const typeLabel =
    data.job_type === KEY_JOB_TYPES.SEAT ? labels.seat : labels.generic;
  const keyJobLabel = data.is_key ? labels.yes : labels.no;

  const renderMeta = () =>
    canSeeSuccession && (
      <div className={classes.meta}>
        <div className={classes.metaItem}>
          <Typography variant="body2" className={classes.label}>
            {labels.jobFamily}
          </Typography>
          <TextBoxWithTooltip
            className={classes.metaValue}
            variant="subtitle2"
            text={familyLabel}
          />
        </div>
        <div className={classes.metaItem}>
          <Typography variant="body2" className={classes.label}>
            {labels.type}
          </Typography>
          <Typography variant="subtitle2" className={classes.metaValue}>
            {typeLabel}
          </Typography>
        </div>
        <div className={classes.metaItem}>
          <Typography variant="body2" className={classes.label}>
            {labels.keyJob}
          </Typography>
          <Typography variant="subtitle2" className={classes.metaValue}>
            {keyJobLabel}
          </Typography>
        </div>
        <div className={classes.metaItem}>
          <Typography variant="body2" className={classes.label}>
            {labels.criticality}
          </Typography>
          <TextBoxWithTooltip
            className={classes.metaValue}
            variant="subtitle2"
            text={criticalityLabel}
          />
        </div>
      </div>
    );

  const renderDescription = () => (
    <div className={classes.descriptionWrapper}>
      <div
        className={classNames(
          classes.labelWrapper,
          classes.titleWrapper,
          classes.descriptionLabelWrapper
        )}
      >
        <Typography variant="body2" className={classes.label}>
          {labels.description}
        </Typography>
        {data.description ? (
          <ActionButton
            type={ACTION_BUTTON_TYPES.PREVIEW_DESCRIPTION}
            tooltipText={labels.preview}
            onClickHandler={handlePreviewClick}
          />
        ) : null}
      </div>
      {data.description ? (
        <RichTextPreview
          className={classes.description}
          text={data.description}
          isEllipsis
        />
      ) : (
        <Typography variant="subtitle2" className={classes.description}>
          -
        </Typography>
      )}
    </div>
  );

  const renderTags = () => (
    <ListWithTooltip
      className={classes.tags}
      listClass={classes.tagsList}
      label={labels.tags}
      items={data.tags}
      asyncListProps={{
        isDisabled: true,
        getItemSize: getVirtualTagItemSize,
      }}
      visibleItemsCount={VISIBLE_ITEMS}
      isTagsList
    />
  );

  return (
    <div
      className={classNames(classes.root, { [classes.readOnly]: isReadOnly })}
      onClick={isReadOnly ? () => {} : onJobTitleClick}
    >
      {renderTitle()}
      {renderMeta()}
      {renderDescription()}
      {renderTags()}
    </div>
  );
};

JobTitleItem.propTypes = {
  familyLabel: PropTypes.string.isRequired,
  criticalityLabel: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  data: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  hasAddButton: PropTypes.bool.isRequired,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  canSeeSuccession: PropTypes.bool.isRequired,
  getUsers: PropTypes.func.isRequired,
  onAddUser: PropTypes.func.isRequired,
  onJobTitleClick: PropTypes.func.isRequired,
  goToUserProfilePage: PropTypes.func.isRequired,
};

export default JobTitleItem;
